import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NavigationSecurity from '../../Layouts/Navigation/NavigationSecurity';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCar, 
  faMotorcycle,
  faParking,
  faCheckCircle,
  faTimesCircle,
  faBuilding,
  faUsers,
  faUserCheck,
  faSquareParking,
  faPhone,
  faXmark,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../utils/constants';

const ParkingControl = () => {
  const [residentParkings, setResidentParkings] = useState([]);
  const [visitorSpaces, setVisitorSpaces] = useState([]);
  const [pendingVisits, setPendingVisits] = useState([]);
  const [visitorStats, setVisitorStats] = useState({
    carros: { total: 0, disponibles: 0, ocupados: 0 },
    motos: { total: 0, disponibles: 0, ocupados: 0 }
  });
  const [residentStats, setResidentStats] = useState({
    total: 0,
    disponibles: 0,
    ocupados: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('visitantes'); // 'visitantes' o 'residentes'
  const [assignModal, setAssignModal] = useState({
    open: false,
    spaceId: null,
    spaceNumber: '',
    spaceType: ''
  });
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [vehicleData, setVehicleData] = useState({
    placa: '',
    marca: '',
    modelo: '',
    color: '',
    visitante_nombre: '',
    visitante_identificacion: '',
    id_visita: null,
    id_usuario: null,
    id_apartamento: null,
    id_torre: null
  });
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' // 'success', 'error', 'warning', 'info'
  });
  const [confirmReleaseDialog, setConfirmReleaseDialog] = useState({
    open: false,
    spaceId: null,
    spaceNumber: ''
  });
  const [refreshSeconds, setRefreshSeconds] = useState(60);

  // Función para mostrar notificaciones
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Auto-refresh cada 60 segundos con contador
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshSeconds(prev => {
        if (prev <= 1) {
          // ejecutar recarga y reiniciar contador
          fetchAllData();
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchResidentParkings(),
      fetchVisitorSpaces(),
      fetchVisitorStats(),
      fetchResidentStats(),
      fetchPendingVisits()
    ]);
    setLoading(false);
  };

  const fetchResidentParkings = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/parkings`);
      setResidentParkings(response.data);
    } catch (error) {
      console.error('Error al obtener parqueaderos de residentes:', error);
    }
  };

  const fetchVisitorSpaces = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/visitorParking/spaces`);
      setVisitorSpaces(response.data);
    } catch (error) {
      console.error('Error al obtener espacios de visitantes:', error);
    }
  };

  const fetchVisitorStats = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/visitorParking/stats`);
      setVisitorStats(response.data);
    } catch (error) {
      console.error('Error al obtener estadísticas de visitantes:', error);
    }
  };

  const fetchResidentStats = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/parkingStats`);
      setResidentStats(response.data);
    } catch (error) {
      console.error('Error al obtener estadísticas de residentes:', error);
    }
  };

  const fetchPendingVisits = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/visitorParking/visits`);
      setPendingVisits(response.data);
    } catch (error) {
      console.error('Error al obtener visitas pendientes:', error);
    }
  };

  const handleOpenAssignModal = (space) => {
    setAssignModal({
      open: true,
      spaceId: space.id,
      spaceNumber: space.numero_espacio,
      spaceType: space.tipo_vehiculo
    });
    setSelectedVisit(null);
    setVehicleData({
      placa: '',
      marca: '',
      modelo: '',
      color: '',
      visitante_nombre: '',
      visitante_identificacion: '',
      id_visita: null,
      id_usuario: null,
      id_apartamento: null,
      id_torre: null
    });
  };

  const handleCloseAssignModal = () => {
    setAssignModal({
      open: false,
      spaceId: null,
      spaceNumber: '',
      spaceType: ''
    });
    setSelectedVisit(null);
  };

  const handleSelectVisit = (visit) => {
    setSelectedVisit(visit);
    setVehicleData({
      placa: visit.placa || '',
      marca: '',
      modelo: '',
      color: '',
      visitante_nombre: `${visit.visitante_nombre} ${visit.visitante_apellido}`,
      visitante_identificacion: '',
      id_visita: visit.id_visita,
      id_usuario: visit.id_usuario,
      id_apartamento: visit.id_apartamento,
      id_torre: visit.id_torre
    });
  };

  const handleVehicleInputChange = (e) => {
    const { name, value } = e.target;
    setVehicleData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignVehicle = async (e) => {
    e.preventDefault();
    
    if (!vehicleData.placa.trim()) {
      showNotification('Por favor ingresa la placa del vehículo', 'warning');
      return;
    }

    if (!vehicleData.id_visita) {
      showNotification('Por favor selecciona una visita para vincular este parqueadero', 'warning');
      return;
    }

    // Validar en frontend: la placa no debe estar ya ocupando otro espacio
    const placaUpper = vehicleData.placa.trim().toUpperCase();
    const espacioConPlaca = visitorSpaces.find(
      s => s.placa_vehiculo && s.placa_vehiculo.toUpperCase() === placaUpper
        && !s.disponible
        && s.id !== assignModal.spaceId
    );
    if (espacioConPlaca) {
      showNotification(
        `La placa "${placaUpper}" ya está asignada al espacio #${espacioConPlaca.numero_espacio}. Libera ese espacio primero.`,
        'error'
      );
      return;
    }

    try {
      await axios.put(`${BASE_URL}/visitorParking/spaces/${assignModal.spaceId}`, {
        disponible: 0,
        placa_vehiculo: vehicleData.placa,
        marca_vehiculo: vehicleData.marca,
        modelo_vehiculo: vehicleData.modelo,
        color_vehiculo: vehicleData.color,
        visitante_nombre: vehicleData.visitante_nombre,
        visitante_identificacion: vehicleData.visitante_identificacion,
        id_visita: vehicleData.id_visita,
        id_usuario: vehicleData.id_usuario,
        id_apartamento: vehicleData.id_apartamento,
        id_torre: vehicleData.id_torre
      });

      showNotification('Vehículo asignado exitosamente', 'success');
      handleCloseAssignModal();
      await fetchAllData();
    } catch (error) {
      console.error('Error al asignar vehículo:', error);
      const msg = error.response?.data?.error || 'Error al asignar vehículo';
      showNotification(msg, 'error');
    }
  };

  const handleReleaseSpace = (space) => {
    setConfirmReleaseDialog({
      open: true,
      spaceId: space.id,
      spaceNumber: space.numero_espacio
    });
  };

  const handleConfirmRelease = async () => {
    const spaceId = confirmReleaseDialog.spaceId;
    setConfirmReleaseDialog({ open: false, spaceId: null, spaceNumber: '' });

    try {
      await axios.put(`${BASE_URL}/visitorParking/spaces/${spaceId}`, {
        disponible: 1,
        placa_vehiculo: null,
        marca_vehiculo: null,
        modelo_vehiculo: null,
        color_vehiculo: null,
        visitante_nombre: null,
        visitante_identificacion: null,
        id_visita: null,
        id_usuario: null,
        id_apartamento: null,
        id_torre: null
      });

      showNotification('Espacio liberado exitosamente', 'success');
      await fetchAllData();
    } catch (error) {
      console.error('Error al liberar espacio:', error);
      showNotification('Error al liberar espacio', 'error');
    }
  };

  const handleCancelRelease = () => {
    setConfirmReleaseDialog({ open: false, spaceId: null, spaceNumber: '' });
  };

  // Función para formatear el tiempo ocupado
  const formatTiempoOcupado = (minutos) => {
    if (!minutos || minutos < 0) return '-';
    
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    
    if (horas === 0) {
      return `${mins} min`;
    } else if (mins === 0) {
      return `${horas}h`;
    } else {
      return `${horas}h ${mins}m`;
    }
  };

  return (
    <>
      <NavigationSecurity />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-gradient-to-br from-security-500 to-security-700 rounded-full">
                <FontAwesomeIcon icon={faParking} className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 text-center mb-2">
              Control de Parqueaderos
            </h1>
            <p className="text-gray-600 text-center">
              Gestión de espacios para visitantes y residentes
            </p>
          </div>

          {/* Alerta de Espacios que Exceden Límite de Tiempo */}
          {visitorSpaces.filter(space => space.excede_tiempo_limite === 1).length > 0 && (
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl shadow-lg p-6 mb-6 animate-pulse">
              <div className="flex items-center mb-3">
                <FontAwesomeIcon icon={faTimesCircle} className="h-8 w-8 mr-3" />
                <div>
                  <h3 className="text-xl font-bold">⚠️ Atención: Espacios Exceden Límite de Tiempo</h3>
                  <p className="text-sm opacity-90">Las visitas con vehículo no deben durar más de 2 horas</p>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="font-semibold text-lg mb-2">
                  {visitorSpaces.filter(space => space.excede_tiempo_limite === 1).length} espacio(s) ocupado(s) por más de 2 horas
                </p>
                <div className="space-y-2">
                  {visitorSpaces
                    .filter(space => space.excede_tiempo_limite === 1)
                    .map(space => (
                      <div key={space.id} className="bg-white bg-opacity-30 rounded p-2 flex justify-between items-center">
                        <div>
                          <span className="font-bold">Espacio #{space.numero_espacio}</span>
                          <span className="mx-2">•</span>
                          <span>{space.placa_vehiculo}</span>
                              {space.torre_nombre && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{space.torre_nombre} - Apto {space.num_apartamento || space.id_apartamento || '-'}</span>
                            </>
                          )}
                        </div>
                        <span className="font-bold bg-white bg-opacity-40 px-3 py-1 rounded">
                          {formatTiempoOcupado(space.minutos_ocupado)}
                        </span>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          )}

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Parqueadero Visitantes Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg">
                  <FontAwesomeIcon icon={faUserCheck} className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-gray-800">Parqueadero Visitantes</h3>
                  <p className="text-sm text-gray-500">Espacios disponibles</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <FontAwesomeIcon icon={faCar} className="text-blue-600 text-2xl" />
                    <span className="text-xs text-gray-500">CARROS</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{visitorStats.carros.disponibles}/{visitorStats.carros.total}</p>
                  <p className="text-xs text-gray-600">Disponibles</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <FontAwesomeIcon icon={faMotorcycle} className="text-blue-600 text-2xl" />
                    <span className="text-xs text-gray-500">MOTOS</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{visitorStats.motos.disponibles}/{visitorStats.motos.total}</p>
                  <p className="text-xs text-gray-600">Disponibles</p>
                </div>
              </div>
            </div>

            {/* Parqueadero Residentes Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg">
                  <FontAwesomeIcon icon={faBuilding} className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-bold text-gray-800">Parqueadero Residentes</h3>
                  <p className="text-sm text-gray-500">Espacios totales</p>
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-600">Total</span>
                  <span className="text-2xl font-bold text-gray-800">{residentStats.total}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-600 text-sm">✓ Disponibles</span>
                  <span className="text-lg font-semibold text-green-600">{residentStats.disponibles}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-red-600 text-sm">✗ Ocupados</span>
                  <span className="text-lg font-semibold text-red-600">{residentStats.ocupados}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab('visitantes')}
                className={`flex-1 px-6 py-4 text-center font-semibold transition-colors ${
                  activeTab === 'visitantes'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FontAwesomeIcon icon={faUserCheck} className="mr-2" />
                Espacios Visitantes
              </button>
              <button
                onClick={() => setActiveTab('residentes')}
                className={`flex-1 px-6 py-4 text-center font-semibold transition-colors ${
                  activeTab === 'residentes'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FontAwesomeIcon icon={faBuilding} className="mr-2" />
                Parqueaderos Residentes
              </button>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-security-600"></div>
                  <p className="mt-4 text-gray-600">Cargando información...</p>
                </div>
              ) : activeTab === 'visitantes' ? (
                // Tabla de Espacios para Visitantes
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Gestión de Espacios de Visitantes</h3>
                      <p className="text-sm text-gray-500 mt-1">Gestión de espacios asignados a visitas</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center bg-amber-50 border border-amber-300 rounded-lg px-4 py-2">
                        <FontAwesomeIcon icon={faClock} className="text-amber-600 mr-2" />
                        <span className="text-sm text-amber-800 font-medium">Límite de tiempo: <strong>2 horas</strong></span>
                      </div>
                      <div className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded px-3 py-1">
                        {refreshSeconds === 60
                          ? 'La información se actualizará en 1 minuto'
                          : `Actualización en ${refreshSeconds}s`}
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">N° Espacio</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Tipo</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Estado</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Tiempo</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Vehículo</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Visitante</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Destino</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {visitorSpaces.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                              No hay espacios de visitantes configurados
                            </td>
                          </tr>
                        ) : (
                          visitorSpaces.map((space) => (
                            <tr key={space.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-semibold text-gray-800">
                                {space.numero_espacio}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  space.tipo_vehiculo === 'CARRO'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-orange-100 text-orange-800'
                                }`}>
                                  <FontAwesomeIcon 
                                    icon={space.tipo_vehiculo === 'CARRO' ? faCar : faMotorcycle} 
                                    className="mr-1"
                                  />
                                  {space.tipo_vehiculo}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {space.disponible ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                    Disponible
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                                    Ocupado
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {!space.disponible && space.minutos_ocupado !== null ? (
                                  <div className="flex items-center gap-2">
                                    {/* Indicador Visual de Tiempo */}
                                    <div className={`w-3 h-3 rounded-full ${
                                      space.excede_tiempo_limite 
                                        ? 'bg-red-500 animate-pulse' 
                                        : space.minutos_ocupado >= 90 
                                        ? 'bg-orange-500' 
                                        : 'bg-green-500'
                                    }`}></div>
                                    
                                    {/* Información de Tiempo */}
                                    <div className="flex flex-col">
                                      <div className="flex items-center gap-1">
                                        <FontAwesomeIcon 
                                          icon={faClock} 
                                          className={`text-xs ${
                                            space.excede_tiempo_limite 
                                              ? 'text-red-600' 
                                              : space.minutos_ocupado >= 90 
                                              ? 'text-orange-600' 
                                              : 'text-gray-600'
                                          }`}
                                        />
                                        <span className={`font-bold text-sm ${
                                          space.excede_tiempo_limite 
                                            ? 'text-red-600' 
                                            : space.minutos_ocupado >= 90 
                                            ? 'text-orange-600' 
                                            : 'text-gray-700'
                                        }`}>
                                          {formatTiempoOcupado(space.minutos_ocupado)}
                                        </span>
                                      </div>
                                      
                                      {/* Etiquetas de Estado */}
                                      {space.excede_tiempo_limite ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 mt-1">
                                          ⚠️ Excedido
                                        </span>
                                      ) : space.minutos_ocupado >= 90 ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 mt-1">
                                          ⏰ Próximo límite
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-500 mt-0.5">
                                          Dentro del límite
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                                    <span className="text-gray-400 text-sm">Sin uso</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {space.placa_vehiculo ? (
                                  <div>
                                    <p className="font-semibold text-gray-800">{space.placa_vehiculo}</p>
                                    <p className="text-xs text-gray-500">
                                      {space.marca_vehiculo} {space.modelo_vehiculo}
                                    </p>
                                    {space.color_vehiculo && (
                                      <p className="text-xs text-gray-400">Color: {space.color_vehiculo}</p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {space.visitante_nombre ? (
                                  <div>
                                    <p className="font-medium text-gray-800">{space.visitante_nombre}</p>
                                    {space.visitante_identificacion && (
                                      <p className="text-xs text-gray-500">ID: {space.visitante_identificacion}</p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {space.torre_nombre ? (
                                  <div>
                                    <p className="font-medium text-gray-800">{space.torre_nombre}</p>
                                    <p className="text-xs text-gray-500">
                                      Apto: {
                                        space.num_apartamento ||
                                        space.num_apto ||
                                        space.apto_numero ||
                                        space.num_apartment ||
                                        space.apartment_number ||
                                        space.apto ||
                                        space.id_apartamento ||
                                        space.id_apto ||
                                        '-'
                                      }
                                    </p>
                                    {space.inquilino_nombre && (
                                      <p className="text-xs text-gray-400">{space.inquilino_nombre} {space.inquilino_apellido}</p>
                                    )}
                                    {space.inquilino_telefono && (
                                      <p className="text-xs text-blue-600 font-medium mt-1">
                                        <FontAwesomeIcon icon={faPhone} className="mr-1" />
                                        {space.inquilino_telefono}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {space.disponible ? (
                                  <button
                                    onClick={() => handleOpenAssignModal(space)}
                                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all text-sm font-medium"
                                  >
                                    Asignar
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleReleaseSpace(space)}
                                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all text-sm font-medium"
                                  >
                                    Liberar
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                // Tabla de Parqueaderos de Residentes
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    Parqueaderos de Residentes
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">N° Parqueadero</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Torre</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Estado</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Vehículo</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Propietario</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Apartamento</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {residentParkings.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                              No hay parqueaderos de residentes configurados
                            </td>
                          </tr>
                        ) : (
                          residentParkings.map((parking) => (
                            <tr key={parking.id_parqueadero} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-semibold text-gray-800">
                                {parking.num_parqueadero}
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {parking.torre_nombre}
                              </td>
                              <td className="px-4 py-3">
                                {parking.estado === 'Disponible' ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                                    Disponible
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                                    Ocupado
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {parking.placa ? (
                                  <div>
                                    <p className="font-semibold text-gray-800">{parking.placa}</p>
                                    <p className="text-xs text-gray-500">
                                      {parking.marca} {parking.modelo}
                                    </p>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {parking.usuario_nombre ? (
                                  <p className="text-gray-700">
                                    {parking.usuario_nombre} {parking.usuario_apellido}
                                  </p>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {parking.num_apartamento || '-'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal para Asignar Vehículo */}
      {assignModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">
                Asignar Vehículo a Espacio {assignModal.spaceNumber} - {assignModal.spaceType}
              </h3>
            </div>
            
            <form onSubmit={handleAssignVehicle} className="p-6">
              <div className="space-y-4">
                {/* Selector de Visita */}
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                  <label className="block text-sm font-bold text-gray-800 mb-3">
                    Seleccionar Visita * (con vehículo {assignModal.spaceType})
                  </label>
                  {pendingVisits.length === 0 ? (
                    <p className="text-sm text-gray-500">No hay visitas pendientes con vehículo registrado</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingVisits
                        .filter(visit => visit.tipo_vehiculo === assignModal.spaceType)
                        .map((visit) => {
                          // Verificar si la placa de esta visita ya está ocupando otro espacio
                          const placaOcupada = visit.placa && visitorSpaces.some(
                            s => s.placa_vehiculo &&
                              s.placa_vehiculo.toUpperCase() === visit.placa.toUpperCase() &&
                              !s.disponible
                          );
                          return (
                            <div
                              key={visit.id_visita}
                              onClick={() => !placaOcupada && handleSelectVisit(visit)}
                              className={`p-3 rounded-lg transition-all ${
                                placaOcupada
                                  ? 'bg-red-50 border border-red-300 cursor-not-allowed opacity-70'
                                  : selectedVisit?.id_visita === visit.id_visita
                                    ? 'bg-blue-600 text-white shadow-lg cursor-pointer'
                                    : 'bg-white hover:bg-blue-100 border border-gray-300 cursor-pointer'
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-semibold flex items-center gap-2">
                                    {visit.visitante_nombre} {visit.visitante_apellido}
                                    {placaOcupada && (
                                      <span className="text-xs font-bold bg-red-200 text-red-800 px-2 py-0.5 rounded-full">
                                        ⚠ Placa ya asignada
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs opacity-90">
                                    Placa: {visit.placa} | {visit.tipo_vehiculo}
                                  </p>
                                  <p className="text-xs opacity-80">
                                    {visit.torre_nombre} - Apto {visit.num_apartamento || visit.id_apartamento || '-'}
                                  </p>
                                  <p className="text-xs opacity-75">
                                    Inquilino: {visit.inquilino_nombre} {visit.inquilino_apellido}
                                  </p>
                                  {visit.inquilino_telefono && (
                                    <p className="text-xs opacity-75 font-medium mt-1">
                                      <FontAwesomeIcon icon={faPhone} className="mr-1" />
                                      Tel: {visit.inquilino_telefono}
                                    </p>
                                  )}
                                </div>
                                {selectedVisit?.id_visita === visit.id_visita && !placaOcupada && (
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-2xl" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {pendingVisits.filter(visit => visit.tipo_vehiculo === assignModal.spaceType).length === 0 && (
                        <p className="text-sm text-gray-500">
                          No hay visitas con vehículo tipo {assignModal.spaceType}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {selectedVisit && (
                  <>
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-bold text-gray-700 mb-3">Información del Vehículo</h4>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Placa del Vehículo *
                        </label>
                        <input
                          type="text"
                          name="placa"
                          value={vehicleData.placa}
                          onChange={handleVehicleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="ABC-123"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Marca
                        </label>
                        <input
                          type="text"
                          name="marca"
                          value={vehicleData.marca}
                          onChange={handleVehicleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Toyota"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Modelo
                        </label>
                        <input
                          type="text"
                          name="modelo"
                          value={vehicleData.modelo}
                          onChange={handleVehicleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Corolla"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Color
                      </label>
                      <input
                        type="text"
                        name="color"
                        value={vehicleData.color}
                        onChange={handleVehicleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Blanco"
                      />
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="text-sm font-bold text-gray-700 mb-3">Información Adicional del Visitante</h4>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Nombre del Visitante
                        </label>
                        <input
                          type="text"
                          name="visitante_nombre"
                          value={vehicleData.visitante_nombre}
                          onChange={handleVehicleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Juan Pérez"
                          readOnly
                        />
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Identificación
                        </label>
                        <input
                          type="text"
                          name="visitante_identificacion"
                          value={vehicleData.visitante_identificacion}
                          onChange={handleVehicleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="123456789"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseAssignModal}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium"
                >
                  Asignar Vehículo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Diálogo de Confirmación de Liberación */}
      {confirmReleaseDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6">
              <h3 className="text-xl font-bold text-white flex items-center">
                <FontAwesomeIcon icon={faTimesCircle} className="mr-3" />
                Confirmar Liberación
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-lg mb-2">
                ¿Está seguro de liberar el espacio <span className="font-bold text-red-600">#{confirmReleaseDialog.spaceNumber}</span>?
              </p>
              <p className="text-gray-500 text-sm">
                Esta acción marcará el espacio como disponible y eliminará la información del vehículo asignado.
              </p>
            </div>
            <div className="flex gap-3 p-6 bg-gray-50">
              <button
                onClick={handleCancelRelease}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmRelease}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all font-medium"
              >
                Liberar Espacio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Componente de Notificación */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[9999] animate-slide-in-right">
          <div className={`rounded-lg shadow-2xl p-4 min-w-[300px] max-w-md flex items-start ${
            notification.type === 'success' ? 'bg-green-500 text-white' :
            notification.type === 'error' ? 'bg-red-500 text-white' :
            notification.type === 'warning' ? 'bg-yellow-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <div className="flex-1">
              <p className="font-semibold text-sm">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
              className="ml-4 text-white hover:text-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ParkingControl;
